import { forwardRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from 'lib/utils';
import { Icon } from 'components/ui/icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import useStore from 'state/store/hooks/useStore';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import useOrgLevel from 'utils/hooks/useOrgLevel';
import routeConfig from 'routes/config';
import accessLocalStorage from 'services/localStorage';
import { ORG_LEVEL_PARAM, SELECTED_ENTITY_IDS_PARAM } from 'utils/constants';
import { oneAppUrl } from 'utils/urlGenerator';
import { ENV } from 'utils/config';
import { Button } from 'components/ui/button';
import { SIDEBAR_EXPANDED_WIDTH } from './config';
import { TRANSITION_DURATION, TRANSITION_EASING } from './nav-item-constants';

interface SidebarStoreSelectorProps {
  isCollapsed?: boolean;
  className?: string;
  onOpenChange?: (open: boolean) => void;
}

const SidebarStoreSelector = forwardRef<
  HTMLDivElement,
  SidebarStoreSelectorProps
>(({ isCollapsed = false, className, onOpenChange }, ref) => {
  const { store } = useStore();
  const { storesList } = store;
  const { organisation } = useOrganisation();
  const { activeOrg } = organisation;
  const isOrgLevel = useOrgLevel();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const activeStoresList = Array.isArray(storesList)
    ? storesList.filter((item) => item.has_access)
    : [];

  const onStoreSelected = (organisationId: string) => {
    accessLocalStorage.setIsOrgLevel(false);
    navigate(
      `${routeConfig.ROOT}${organisationId}?${ORG_LEVEL_PARAM.KEY}=false`
    );
  };

  const onOrgViewSelected = () => {
    accessLocalStorage.setIsOrgLevel(true);
    accessLocalStorage.setSelectedEntityIds([]);

    const search = new URLSearchParams(location.search);
    search.set(ORG_LEVEL_PARAM.KEY, ORG_LEVEL_PARAM.VALUE);
    search.delete(SELECTED_ENTITY_IDS_PARAM.KEY);

    navigate({ pathname: location.pathname, search: search.toString() });
  };

  const displayText = isOrgLevel
    ? 'Org view'
    : activeOrg?.name || 'Select Store';

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  return (
    <div ref={ref} className={cn('pb-2', className)}>
      <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="muted"
            rounded="none"
            className={cn(
              'w-full flex items-center py-4 px-2',
              isCollapsed ? 'justify-center' : ''
            )}
            iconStart="store"
            iconSize={20}
            disabled={!activeOrg}
            size={'md'}
            data-testid="selectedStoreBtn"
            aria-label={isCollapsed ? 'Store selector' : undefined}
          >
            {!isCollapsed && (
              <>
                <span
                  className={cn(
                    `truncate overflow-hidden transition-all ${TRANSITION_DURATION} ${TRANSITION_EASING}`,
                    'opacity-100 max-w-48 ml-2'
                  )}
                >
                  {displayText}
                </span>
                <Icon
                  name="chevron-down"
                  size={16}
                  className={cn(
                    `shrink-0 transition-all ${TRANSITION_DURATION} ${TRANSITION_EASING}`,
                    'opacity-100 ml-auto',
                    isOpen && 'rotate-180'
                  )}
                />
              </>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side="top"
          align="start"
          className="max-h-96 z-50"
          style={{ width: SIDEBAR_EXPANDED_WIDTH }}
          sideOffset={4}
        >
          <DropdownMenuGroup>
            <DropdownMenuItem
              onClick={onOrgViewSelected}
              className={cn(
                'cursor-pointer',
                isOrgLevel && 'bg-gray-blue-10 font-medium'
              )}
              data-testid="orgViewItem"
            >
              <Icon name="building" size={16} />
              <span>Org view</span>
              {isOrgLevel && (
                <Icon name="check" size={16} className="ml-auto" />
              )}
            </DropdownMenuItem>
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuGroup>
            {activeStoresList.map((item) => {
              const isActive =
                !isOrgLevel && item.organization_id === activeOrg?.id;
              return (
                <DropdownMenuItem
                  key={item.organization_id}
                  onClick={() => onStoreSelected(item.organization_id)}
                  className={cn(
                    'cursor-pointer',
                    isActive && 'bg-gray-blue-10 font-medium'
                  )}
                  data-testid="storeListItem"
                >
                  <span className="truncate">{item.dba_name}</span>
                  {isActive && (
                    <Icon name="check" size={16} className="shrink-0 ml-auto" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            asChild
            className="cursor-pointer text-dark-green-link"
          >
            <a
              href={oneAppUrl(ENV || 'production')}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="plus" size={16} />
              <span>Add another store</span>
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
});

SidebarStoreSelector.displayName = 'SidebarStoreSelector';

export { SidebarStoreSelector };
